import { TradingAccountInput } from "./types";
import { gatewayLogoUrl } from "./brokerLogo";

/**
 * The payload carried by the "Open in terminal" email deep-link (`?connect=<token>`), minted and signed by
 * the CRM (`ConnectTokenService`). The token is `base64url(json).base64url(hmac)`.
 *
 * TRUST MODEL: the terminal cannot safely hold the HMAC secret, so it does NOT verify the signature here —
 * it treats the payload as trusted-on-arrival and only validates its shape + expiry. The signature is an
 * integrity check for the mail transport, not a client-side auth gate; the credentials inside are the same
 * ones printed in the email body the user already received. (An optional CRM verify endpoint could return
 * the payload for end-to-end integrity, but that's out of scope here — see the proposal's Against list.)
 */
export interface ConnectPayload {
  login: number;
  password: string;
  host: string;
  port: number;
  hubUrl?: string;
  gatewaySlug?: string;
  /**
   * Branded company logo (absolute URL or data-URI) minted into the token by the CRM so the account renders with
   * the right mark on the FIRST paint, before `backfillBrokerLogos()` reaches the gateway catalog (proposal 70).
   * Optional — when absent the catalog backfill remains the only resolution path, exactly as before.
   */
  logoUrl?: string;
  companyName: string;
  serverName: string;
  mode: string;
  ccy: string;
  /** Expiry, unix seconds. */
  exp: number;
}

function base64UrlDecodeToJson(part: string): string {
  const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Decode + validate a connect token's payload. Returns null when the token is malformed, missing required
 * fields, or expired. The HMAC signature part is intentionally ignored client-side (see the trust note above).
 */
export function decodeConnectToken(token: string): ConnectPayload | null {
  try {
    const body = token.split(".")[0];
    if (!body) return null;

    const parsed = JSON.parse(base64UrlDecodeToJson(body)) as Partial<ConnectPayload>;

    const loginOk = typeof parsed.login === "number" && Number.isFinite(parsed.login);
    const shapeOk =
      loginOk &&
      typeof parsed.password === "string" &&
      typeof parsed.host === "string" &&
      typeof parsed.port === "number" &&
      typeof parsed.exp === "number";
    if (!shapeOk) return null;

    // Expired links are rejected — never auto-connect on a stale credential.
    if ((parsed.exp as number) * 1000 < Date.now()) return null;

    return {
      login: parsed.login as number,
      password: parsed.password as string,
      host: parsed.host as string,
      port: parsed.port as number,
      hubUrl: parsed.hubUrl || undefined,
      gatewaySlug: parsed.gatewaySlug || undefined,
      logoUrl: parsed.logoUrl || undefined,
      companyName: parsed.companyName || "Vybe Trader",
      serverName: parsed.serverName || (parsed.host as string),
      mode: parsed.mode || "",
      ccy: parsed.ccy || "",
      exp: parsed.exp as number,
    };
  } catch {
    return null;
  }
}

/** Map a decoded payload onto the terminal's `TradingAccount` shape (always a Vybe gateway account). */
export function connectPayloadToAccount(p: ConnectPayload): TradingAccountInput {
  return {
    companyName: p.companyName,
    serverName: p.serverName,
    providerKind: "Vybe",
    credentials: { login: String(p.login), password: p.password },
    createdAt: Date.now(),
    lastConnectedAt: 0,
    host: p.host,
    port: p.port,
    hubUrl: p.hubUrl,
    gatewaySlug: p.gatewaySlug,
    // Always a Vybe-gateway account: store our bundled mark (never the gateway's remote /brand url, and
    // never nothing) so the tile paints offline and on a cold PWA start — see lib/accounts/brokerLogo.ts.
    brokerLogoUrl: gatewayLogoUrl({
      logoUrl: p.logoUrl,
      gatewaySlug: p.gatewaySlug ?? "sig-vybe",
      companyName: p.companyName,
    }),
  };
}
